"use client";

import { createContext, ReactNode, useContext, useState } from "react";

type EventModalType =
    | "webinar"
    | "giveaway"
    | "loginRequired"
    | "premiumRequired"
    | null;

interface EventModalData {
    eventId: number;
    eventTitle: string;
}

interface EventFormContextType {
    isAnyModalOpen: boolean;
    activeModal: EventModalType;
    modalData: EventModalData | null;
    openWebinarModal: (data: EventModalData) => void;
    openGiveawayModal: (data: EventModalData) => void;
    openLoginRequiredModal: () => void;
    openPremiumRequiredModal: () => void;
    closeModal: () => void;
}

const EventFormContext = createContext<EventFormContextType | undefined>(
    undefined,
);

export function EventFormProvider({ children }: { children: ReactNode }) {
    const [activeModal, setActiveModal] = useState<EventModalType>(null);
    const [modalData, setModalData] = useState<EventModalData | null>(null);

    const openWebinarModal = (data: EventModalData) => {
        setActiveModal("webinar");
        setModalData(data);
    };

    const openGiveawayModal = (data: EventModalData) => {
        setActiveModal("giveaway");
        setModalData(data);
    };

    const openLoginRequiredModal = () => {
        setActiveModal("loginRequired");
        setModalData(null);
    };

    const openPremiumRequiredModal = () => {
        setActiveModal("premiumRequired");
        setModalData(null);
    };

    const closeModal = () => {
        setActiveModal(null);
        setModalData(null);
    };

    return (
        <EventFormContext.Provider
            value={{
                isAnyModalOpen: activeModal !== null,
                activeModal,
                modalData,
                openWebinarModal,
                openGiveawayModal,
                openLoginRequiredModal,
                openPremiumRequiredModal,
                closeModal,
            }}
        >
            {children}
        </EventFormContext.Provider>
    );
}

export function useEventForm() {
    const context = useContext(EventFormContext);
    if (context === undefined) {
        throw new Error("useEventForm must be used within EventFormProvider");
    }
    return context;
}
